import React from 'react';
import { ExternalLink, Eye, Sparkles } from 'lucide-react';
import {
  IMPROVE,
  IMPROVE_SUMMARY,
  IMPROVE_TITLE,
  KEEP,
  KEEP_SUMMARY_AVAILABLE,
  KEEP_SUMMARY_UNAVAILABLE,
  KEEP_TITLE,
} from '@/lib/importFinishCopy';

export default function BringOverChoice({
  finish,
  onChange,
  keepAvailable = false,
  sourceUrl = '',
  capture = null,
  testIdPrefix = 'import',
}) {
  const keepSummary = keepAvailable ? KEEP_SUMMARY_AVAILABLE : KEEP_SUMMARY_UNAVAILABLE;
  const pages = (capture?.pages || []).slice(0, 6);

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-finish`}>
      <div>
        <h3 className="text-sm font-medium text-slate-200 mb-1">How should we bring it over?</h3>
        <p className="text-xs text-slate-500">
          You choose. Redesign builds a new site from what we read.
          {keepAvailable
            ? ' Keep this layout brings your current pages over so you can edit them.'
            : ' Keep this layout is preview-only until those pages can be brought over as they are.'}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => onChange(KEEP)}
          className={`text-left rounded-lg border p-4 transition-all ${
            finish === KEEP
              ? 'border-indigo-500 bg-indigo-500/10'
              : 'border-slate-700 bg-slate-900/40 hover:border-slate-600'
          }`}
          data-testid={`${testIdPrefix}-finish-keep`}
        >
          <div className="flex items-center gap-2 mb-2">
            <Eye className="w-4 h-4 text-indigo-300" />
            <span className="text-sm font-medium text-white">{KEEP_TITLE}</span>
          </div>
          <p className="text-xs text-slate-400">{keepSummary}</p>
          {!keepAvailable && (
            <p className="text-[11px] text-amber-300/90 mt-2" data-testid={`${testIdPrefix}-keep-unavailable`}>
              Not offered yet — preview only.
            </p>
          )}
        </button>

        <button
          type="button"
          onClick={() => onChange(IMPROVE)}
          className={`text-left rounded-lg border p-4 transition-all ${
            finish === IMPROVE
              ? 'border-indigo-500 bg-indigo-500/10'
              : 'border-slate-700 bg-slate-900/40 hover:border-slate-600'
          }`}
          data-testid={`${testIdPrefix}-finish-improve`}
        >
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-emerald-300" />
            <span className="text-sm font-medium text-white">{IMPROVE_TITLE}</span>
          </div>
          <p className="text-xs text-slate-400">{IMPROVE_SUMMARY}</p>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid={`${testIdPrefix}-previews`}>
        <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-slate-300">Current site</p>
            {sourceUrl && (
              <a
                href={sourceUrl}
                target="_blank"
                rel="noreferrer"
                className="text-indigo-400 hover:text-indigo-300 text-xs inline-flex items-center gap-1"
                data-testid={`${testIdPrefix}-current-site-link`}
              >
                Open <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          {sourceUrl ? (
            <div className="rounded-md overflow-hidden border border-slate-800 bg-slate-950 aspect-video">
              <iframe
                title="Current site preview"
                src={sourceUrl}
                sandbox="allow-scripts allow-same-origin"
                className="w-full h-full bg-white"
                data-testid={`${testIdPrefix}-current-iframe`}
              />
            </div>
          ) : (
            <p className="text-xs text-slate-500">Read a site to preview the current layout.</p>
          )}
        </div>

        <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-3">
          <p className="text-xs font-medium text-slate-300 mb-2">What a redesign would use</p>
          {pages.length > 0 ? (
            <ul className="space-y-1" data-testid={`${testIdPrefix}-redesign-pages`}>
              {pages.map((page, i) => (
                <li key={page.url || i} className="text-xs text-slate-400 truncate">
                  {page.title || page.url}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-slate-500">
              Pages, photos, and products we read will show here after we read the site.
            </p>
          )}
          {capture && (
            <p className="text-[11px] text-slate-500 mt-2">
              {capture.page_count || pages.length || 0} pages · {capture.photo_count || 0} photographs · {capture.product_count || 0} products
              {capture.photo_count > 0 ? " — photographs go into your editor library" : ""}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
